import { FunctionComponent, ReactElement, ChangeEvent, useState, useEffect, useMemo } from 'react';
import { Stack } from '@mui/material';
import FormDialog from '@/components/ui/form-dialog';
import { Device } from '@/core/entities';
import TextField from '@/components/ui/form-inputs/TextField';
import { DEVICE_NAME_FORM_KEY } from '@/shared/constants/form-keys';
import useToastMessage from '@/shared/context/toast-message-context';
import { useUpdateDeviceName } from '@/queries/devices';
import { pushToDataLayer, ContentGroup } from '@/shared/utils/analytics';

interface DeviceNameUpdateFormProps {
	open: boolean;
	onClose: () => void;
	device: Device;
	onSubmitted?: () => Promise<void> | void;
	customerName?: string;
}

const DeviceNameUpdateForm: FunctionComponent<DeviceNameUpdateFormProps> = ({
	open,
	onClose,
	device,
	onSubmitted,
	customerName,
}): ReactElement => {
	const { dispatch } = useToastMessage();
	const [deviceName, setDeviceName] = useState<string>('');

	useEffect(() => {
		if (device) {
			setDeviceName(device.name);
		}
	}, [device]);

	const onFormClose = () => {
		setDeviceName('');
		onClose();
	};

	const trackDeviceNameUpdate = () => {
		pushToDataLayer({
			event: 'device_edited',
			content_group: ContentGroup.Customers,
			customer_name: customerName,
		});
	};

	const { isLoading: isUpdateNameFormSubmitting, mutate: onUpdateDeviceNameSubmit } =
		useUpdateDeviceName({
			onSuccess: async () => {
				await onSubmitted?.();
				onFormClose();
				dispatch({
					type: 'success',
					content: 'Device name successfully updated',
				});
				trackDeviceNameUpdate();
			},
		});

	const onSubmit = () => {
		onUpdateDeviceNameSubmit({
			id: device.id,
			request: {
				request: { name: deviceName },
				formKey: DEVICE_NAME_FORM_KEY,
			},
		});
	};

	const isFormDisabled: boolean = useMemo(() => !deviceName.length, [deviceName]);

	return (
		<FormDialog
			formKey={DEVICE_NAME_FORM_KEY}
			title="Edit device"
			open={open}
			onClose={onFormClose}
			fullWidth
			maxWidth="xs"
			actions={{
				cancel: {
					label: 'Cancel',
				},
				confirm: {
					label: 'Save',
					disabled: isFormDisabled,
					isLoading: isUpdateNameFormSubmitting,
				},
			}}
			onSubmit={onSubmit}
		>
			<Stack direction="column" spacing={2}>
				{device && (
					<TextField
						id="deviceName"
						name="deviceName"
						label="Device name"
						formKey={DEVICE_NAME_FORM_KEY}
						required
						fullWidth
						value={deviceName}
						onChange={(event: ChangeEvent<HTMLInputElement>) => setDeviceName(event.target.value)}
					/>
				)}
			</Stack>
		</FormDialog>
	);
};

export default DeviceNameUpdateForm;
